import { Box } from "@mui/material";
import { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import AccessDenied from "../../../components/access_denied/access_denied";
import CmsLayout from "../../../layouts/cms/cms_layout";
import MainLayout from "../../../layouts/main/main_layout";
import { useFeedback } from "../../../providers/feedback/feeedback";
import { queryClient } from "../../../providers/query/query";
import useUser from "../../../providers/user/user_provider";
import { deleteAmiForYou, getAmiForYouList } from "../../../services/cms/cms";
import DeleteCmsItemDialog from "../components/delete_cms_item/delete_cms_item";
import FilterAmiForYou, { AmiForYouFilterValues } from "../components/filter_amiforyou/filter_amiforyou";
import AmiForYouListing, { StatusFilter } from "../components/amiforyou_listing/amiforyou_listing";
import SortAmiForYouPopup, {
  DEFAULT_AMIFORYOU_SORT_OPTION,
  AmiForYouSortOption,
  amiForYouSortOptionLabel,
  amiForYouSortOptionToParams,
} from "../components/sort_amiforyou_popup/sort_amiforyou_popup";
import { AmiForYou } from "../models/cms";
import { useSelectedOrgStore } from "../../../store/use-selected-org-store";

const ROWS_PER_PAGE = 10;

export default function AmiForYouPage() {
  const { hasPermission } = useUser();
  const { showSnackbar } = useFeedback();
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [sortOption, setSortOption] = useState<AmiForYouSortOption>(DEFAULT_AMIFORYOU_SORT_OPTION);
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AmiForYou | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { selectedOrgId } = useSelectedOrgStore();

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchValue(searchValue), 500);
    return () => clearTimeout(handler);
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchValue, statusFilter, dateFrom, dateTo, selectedOrgId, sortOption]);

  const fromDate = dateFrom?.format("YYYY-MM-DD");
  const toDate = dateTo?.format("YYYY-MM-DD");

  const { data, isLoading, isFetching } = useQuery(
    ["cms-amiforyou", page, debouncedSearchValue, statusFilter, selectedOrgId, sortOption, fromDate, toDate],
    async () => {
      const { sortBy, sortOrder } = amiForYouSortOptionToParams(sortOption);
      const response = await getAmiForYouList({
        page,
        size: ROWS_PER_PAGE,
        search: debouncedSearchValue,
        status: statusFilter === "all" ? undefined : statusFilter === "active",
        orgId: selectedOrgId || undefined,
        sortBy,
        sortOrder,
        fromDate,
        toDate,
      });
      return response?.data?.data;
    },
    { keepPreviousData: true }
  );

  const list: AmiForYou[] = data?.list || [];
  const count = data?.total || 0;

  const filterCount = [statusFilter !== "all", !!dateFrom && !!dateTo].filter(Boolean).length;

  const handleApplyFilters = (values: AmiForYouFilterValues) => {
    setStatusFilter(values.statusFilter);
    setDateFrom(values.dateFrom);
    setDateTo(values.dateTo);
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    deleteAmiForYou(deleteTarget.id)
      .then(() => {
        showSnackbar("AMI For You item deleted successfully");
        queryClient.invalidateQueries({ queryKey: ["cms-amiforyou"] });
      })
      .catch((e) => {
        showSnackbar(e?.response?.data?.error?.message || "Something went wrong");
      })
      .finally(() => {
        setIsDeleting(false);
        setDeleteTarget(null);
      });
  };

  return (
    <MainLayout>
      {hasPermission("cms", "read") ? (
        <CmsLayout>
          <AmiForYouListing
            list={list}
            count={count}
            page={page}
            setPage={setPage}
            isLoading={isLoading || isFetching}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            filterCount={filterCount}
            onOpenFilters={() => setFilterDialogOpen(true)}
            sortLabel={amiForYouSortOptionLabel(sortOption)}
            onOpenSort={() => setSortDialogOpen(true)}
            onDelete={setDeleteTarget}
          />
          <DeleteCmsItemDialog
            open={!!deleteTarget}
            itemName={deleteTarget?.title}
            isDeleting={isDeleting}
            onClose={() => setDeleteTarget(null)}
            onConfirm={handleDeleteConfirm}
          />
          {filterDialogOpen && (
            <FilterAmiForYou
              open={filterDialogOpen}
              onClose={() => setFilterDialogOpen(false)}
              statusFilter={statusFilter}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onApply={handleApplyFilters}
            />
          )}
          <SortAmiForYouPopup
            open={sortDialogOpen}
            onClose={() => setSortDialogOpen(false)}
            sortOption={sortOption}
            setSortOption={setSortOption}
          />
        </CmsLayout>
      ) : (
        <Box display="flex" flex={1} width="100%" height="100%">
          <AccessDenied />
        </Box>
      )}
    </MainLayout>
  );
}
